import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { resolveContentOrigin } from './isolatedFrame';

/**
 * The session's user-content origin (ADR 0078), as the session provider last
 * heard it. Undefined — no provider, no session, a server without one — keeps
 * every message in the srcdoc frame, which is what every test and the mock
 * standalone mode render.
 */
const ContentOriginContext = createContext<string | undefined>(undefined);

export function ContentOriginProvider({
  origin,
  children,
}: {
  origin: string | undefined;
  children: ReactNode;
}) {
  return <ContentOriginContext.Provider value={origin}>{children}</ContentOriginContext.Provider>;
}

/**
 * The origin this page may frame message bodies from, or null for the srcdoc
 * renderer. Resolved against this build's own allowed origin
 * (`resolveContentOrigin`), so a server/build mismatch degrades instead of
 * drawing a frame the app's CSP would refuse.
 */
export function useContentOrigin(): string | null {
  const advertised = useContext(ContentOriginContext);
  return useMemo(() => resolveContentOrigin(advertised), [advertised]);
}
